import { createSelector } from '@/redux/lib/createSelector.js';
import memoizeSelector from '@/redux/lib/memoizeSelector.js';
import { removeArrayContent } from '@shared/utils/removeArrayContent.js';
import selectors from './selectors.js';
import { param } from './uiSelectors.js';

export const isSuggestionsLoading = createSelector(
  'isSuggestionsLoading',
  [(state) => !!state?.ai?.suggestionsLoading, (state) => selectors.isLoading(state, 'getAiSuggestions')], //
  (isLoading, isFetching) => isLoading || isFetching
);

// For now, since we only have app suggestions, I'm filtering by appId
// When we have more, we'll need to update this selector
export const aiSuggestions = memoizeSelector(
  'suggestions',
  createSelector(
    'suggestions',
    [(state) => state?.ai?.suggestions, (state) => param(state, 'appId')], //
    (suggestions, appId) => suggestions?.filter?.((n) => n?.objectId === appId)?.filter?.((n) => !n?.isCustom)
  )
);

export const customAiSuggestions = memoizeSelector(
  'customAiSuggestions',
  createSelector(
    'customAiSuggestions',
    [(state) => state?.ai?.suggestions, (state) => param(state, 'appId')], //
    (suggestions, appId) => suggestions?.filter?.((n) => n?.objectId === appId)?.filter?.((n) => n?.isCustom === true)
  )
);

export const actionSuggestions = memoizeSelector(
  'actionSuggestions',
  createSelector(
    'actionSuggestions',
    [customAiSuggestions], //
    (suggestions) => {
      const suggestion = suggestions?.find?.((n) => n?.path?.endsWith('suggestions.actions'));
      const actions = suggestion?.suggestion?.suggestedValue;

      return {
        totalActions: actions?.suggestedActions?.length + actions?.otherActions?.length,
        suggestedActions: (actions?.suggestedActions || [])?.map((n) => ({ ...n, suggestionId: suggestion?.id, suggestionPath: suggestion?.path, meta: suggestion?.meta })),
        otherActions: (actions?.otherActions || [])?.map((n) => ({ ...n, suggestionId: suggestion?.id, suggestionPath: suggestion?.path, meta: suggestion?.meta }))
      };
    }
  )
);

export const aiSuggestionsByPath = memoizeSelector(
  'aiSuggestionsByPath',
  createSelector(
    'aiSuggestionsByPath',
    [aiSuggestions, (state) => param(state, 'section'), (state) => param(state, 'arg1'), (state) => param(state, 'arg3'), (_, path) => path], //
    (suggestions, section, actionId, argumentId, path) => {
      const getBaseSuggestion = () => {
        if (!path) {
          return suggestions;
        }
        if (Array.isArray(path)) {
          return suggestions?.filter((n) => path.some((p) => removeArrayContent(n?.path) === removeArrayContent(p)));
        }
        return suggestions?.filter((n) => removeArrayContent(n?.path) === removeArrayContent(path));
      };

      const baseSuggestions = getBaseSuggestion();

      if (section === 'actions') {
        return baseSuggestions?.filter((n) => {
          const hasArgumentId = n?.path?.includes('arguments[id:');
          const suggestionActionId = n?.path?.split('actions[id:')[1]?.split(']')[0];

          if (hasArgumentId) {
            const suggestionArgumentId = n?.path?.split('arguments[id:')[1]?.split(']')[0];
            return suggestionActionId === actionId && suggestionArgumentId === argumentId;
          }
          return suggestionActionId === actionId;
        });
      }

      return baseSuggestions;
    }
  )
);

export const actionArgumentListSuggestions = memoizeSelector(
  'actionArgumentListSuggestions',
  createSelector(
    'actionArgumentListSuggestions',
    [customAiSuggestions, (state) => param(state, 'arg1')], //
    (suggestions, actionId) => {
      const suggestion = suggestions?.filter((n) => n?.path?.split('actions[id:')[1]?.split(']')[0] === actionId && n?.path?.endsWith('suggestions.arguments'))?.[0];
      return suggestion?.suggestion?.suggestedValue?.map?.((n) => ({
        ...n,
        suggestionId: suggestion?.id,
        suggestionPath: suggestion?.path,
        meta: suggestion?.meta
      }));
    }
  )
);

export const docsLoadingByAppId = createSelector('docsLoadingByAppId', [(state) => state?.ai?.docsLoading, (state) => param(state, 'appId')], (docsLoading, appId) => docsLoading?.[appId]);

const VERSION_ORDER = ['summary', 'full', 'raw'];
const RESEARCH_DOC_TYPES = ['authentication', 'filtering', 'pagination', 'sorting'];
const APP_DRAWER_SECTION_ORDER = ['app', 'filtering', 'pagination', 'sorting'];

const mapRouteSectionToDocType = (section) => {
  if (!section) {
    return 'app';
  }
  if (section === 'actions') {
    return 'action';
  }
  if (section === 'auth') {
    return 'authentication';
  }
  return section;
};

const normalizeTypePrimaryKey = (type, objectId) => {
  if (type === 'app') {
    return null;
  }

  if (RESEARCH_DOC_TYPES.includes(type) && (objectId === null || objectId === undefined || objectId === '' || objectId === 'default')) {
    return null;
  }

  return objectId;
};

const filterRowsByTypeAndObject = ({ appDoc, appId, type, objectId }) => {
  if (!appId || !type) {
    return [];
  }

  const typePrimaryKey = normalizeTypePrimaryKey(type, objectId);
  return appDoc?.filter((n) => n?.appId === appId && n?.type === type && (typePrimaryKey == null ? n?.typePrimaryKey == null : n?.typePrimaryKey === typePrimaryKey)) ?? [];
};

const getOrderedVersions = (rows) => {
  const versions = [...new Set(rows.map((r) => r.version).filter(Boolean))];
  return VERSION_ORDER.filter((v) => versions.includes(v));
};

const pickPrimaryDoc = (rows) => {
  if (!rows?.length) return null;
  const summary = rows.find((r) => r.version === 'summary');
  return summary ?? rows[0];
};

export const appActionDoc = createSelector(
  'appActionDoc',
  [(state) => state?.ai?.appDoc, (state) => param(state, 'appId'), (state) => param(state, 'arg1')], //
  (appDoc, appId, actionId) => {
    const rows = filterRowsByTypeAndObject({ appDoc, appId, type: 'action', objectId: actionId });
    return pickPrimaryDoc(rows);
  }
);

export const appDoc = createSelector(
  'appDoc',
  [(state) => state?.ai?.appDoc, (state) => param(state, 'appId')], //
  (appDoc, appId) => {
    const rows = filterRowsByTypeAndObject({ appDoc, appId, type: 'app', objectId: null });
    return pickPrimaryDoc(rows);
  }
);

export const appDocByTypeAndObject = createSelector(
  'appDocByTypeAndObject',
  [(state) => state?.ai?.appDoc, (state) => param(state, 'appId'), (_, type) => type, (_, __, objectId) => objectId], //
  (appDoc, appId, type, objectId) => {
    const rows = filterRowsByTypeAndObject({ appDoc, appId, type, objectId });
    return pickPrimaryDoc(rows);
  }
);

export const appDocAvailableVersionsByTypeAndObject = createSelector(
  'appDocAvailableVersionsByTypeAndObject',
  [(state) => state?.ai?.appDoc, (state) => param(state, 'appId'), (_, type) => type, (_, __, objectId) => objectId], //
  (appDoc, appId, type, objectId) => {
    const rows = filterRowsByTypeAndObject({ appDoc, appId, type, objectId });
    return getOrderedVersions(rows);
  }
);

export const appDocAvailableAppSections = createSelector(
  'appDocAvailableAppSections',
  [(state) => state?.ai?.appDoc, (state) => param(state, 'appId')], //
  (appDoc, appId) =>
    APP_DRAWER_SECTION_ORDER.filter((type) => {
      const rows = filterRowsByTypeAndObject({ appDoc, appId, type, objectId: null });
      return getOrderedVersions(rows).length > 0;
    })
);

export const appDocAvailableVersions = createSelector(
  'appDocAvailableVersions',
  [(state) => state?.ai?.appDoc, (state) => param(state, 'appId'), (state) => param(state, 'section'), (state) => param(state, 'arg1')], //
  (appDoc, appId, section, arg1) => {
    const type = mapRouteSectionToDocType(section);
    const objectId = type === 'app' ? null : arg1;
    const rows = filterRowsByTypeAndObject({ appDoc, appId, type, objectId });
    return getOrderedVersions(rows);
  }
);
